import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { calculateInvoiceSummary, normalizeInvoiceItems } from './invoiceCalculations';
import { scaleLogoDimensions } from '../../../../utils/organizationLogoScaling';

const sanitizeEpc = (value: string | null | undefined) => {
  if (!value) return '';
  const normalized = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim();
  return normalized.slice(0, 70);
};

const buildEpcPayload = ({
  bic,
  creditorName,
  iban,
  amount,
  remittance,
}: {
  bic?: string | null;
  creditorName: string;
  iban: string;
  amount: number;
  remittance: string;
}) => {
  const sanitizedName = sanitizeEpc(creditorName);
  const sanitizedRemittance = sanitizeEpc(remittance);
  const normalizedIban = iban.replace(/\s+/g, '').toUpperCase();
  const amountFormatted = Number(amount).toFixed(2);
  return [
    'BCD',
    '001',
    '1',
    'SCT',
    bic ? bic.replace(/\s+/g, '').toUpperCase() : '',
    sanitizedName,
    normalizedIban,
    `EUR${amountFormatted}`,
    '',
    sanitizedRemittance,
    '',
  ].join('\r\n');
};

const joinAddress = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' ');

type InvoicePriceDisplayMode = 'net' | 'gross';

type DocumentTextType =
  | 'invoice'
  | 'quote'
  | 'order_confirmation'
  | 'delivery_note'
  | 'partial_invoice'
  | 'credit_note'
  | 'recurring_invoice';

interface DocumentTextConfig {
  titleKey: string;
  introKey: string;
  footerKey: string;
  paymentTermsKey?: string;
  numberLabel: string;
  fallbackTitle: string;
  fallbackIntro: string;
  fallbackFooter: string;
  fallbackPaymentTerms?: string;
}

const removeDiacritics = (value: string) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

// Erkennt mobile/touch-basierte Geräte, damit der PDF-Export dort mit kompatiblem Fallback geöffnet wird.
const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  return (
    navigator.maxTouchPoints > 0 ||
    window.matchMedia?.('(pointer: coarse)').matches ||
    /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent)
  );
};

const detectDocumentType = (invoice: any): DocumentTextType => {
  const rawType = [
    invoice?.document_type,
    invoice?.document_kind,
    invoice?.document_category,
    invoice?.type,
    invoice?.document_template,
    invoice?.document
  ].find((entry) => typeof entry === 'string' && entry.trim().length > 0);

  if (!rawType) {
    return 'invoice';
  }

  const normalized = removeDiacritics(String(rawType).toLowerCase())
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');

  switch (normalized) {
    case 'quote':
    case 'angebot':
      return 'quote';
    case 'order-confirmation':
    case 'auftragsbestaetigung':
    case 'orderconfirmation':
      return 'order_confirmation';
    case 'delivery-note':
    case 'lieferschein':
    case 'deliverynote':
      return 'delivery_note';
    case 'partial':
    case 'partial-invoice':
    case 'abschlagsrechnung':
    case 'down-payment':
    case 'progress-invoice':
      return 'partial_invoice';
    case 'correction':
    case 'credit-note':
    case 'rechnungskorrektur':
    case 'gutschrift':
      return 'credit_note';
    case 'recurring':
    case 'recurring-invoice':
    case 'serienrechnung':
      return 'recurring_invoice';
    default:
      return 'invoice';
  }
};

const DOCUMENT_TEXT_CONFIG: Record<DocumentTextType, DocumentTextConfig> = {
  invoice: {
    titleKey: 'invoice_document_title',
    introKey: 'invoice_intro_text',
    footerKey: 'invoice_footer_text',
    paymentTermsKey: 'payment_terms',
    numberLabel: 'Rechnungsnr.',
    fallbackTitle: 'Rechnung',
    fallbackIntro: 'Unsere Leistungen stellen wir Ihnen wie folgt in Rechnung.',
    fallbackFooter: 'Vielen Dank für Ihr Vertrauen.',
    fallbackPaymentTerms:
      'Bitte überweisen Sie den Rechnungsbetrag unter Angabe der Rechnungsnummer fristgerecht auf das untenstehende Konto.'
  },
  quote: {
    titleKey: 'quote_document_title',
    introKey: 'quote_intro_text',
    footerKey: 'quote_footer_text',
    numberLabel: 'Angebotsnr.',
    fallbackTitle: 'Angebot',
    fallbackIntro: 'Gerne unterbreiten wir Ihnen folgendes Angebot.',
    fallbackFooter: 'Dieses Angebot ist 14 Tage gültig. Wir freuen uns auf Ihre Rückmeldung.'
  },
  order_confirmation: {
    titleKey: 'order_confirmation_document_title',
    introKey: 'order_confirmation_intro_text',
    footerKey: 'order_confirmation_footer_text',
    numberLabel: 'Auftragsnr.',
    fallbackTitle: 'Auftragsbestätigung',
    fallbackIntro: 'Hiermit bestätigen wir Ihren Auftrag.',
    fallbackFooter: 'Vielen Dank für Ihr Vertrauen.'
  },
  delivery_note: {
    titleKey: 'delivery_note_document_title',
    introKey: 'delivery_note_intro_text',
    footerKey: 'delivery_note_footer_text',
    numberLabel: 'Lieferscheinnr.',
    fallbackTitle: 'Lieferschein',
    fallbackIntro: 'Wir bestätigen die Lieferung folgender Artikel.',
    fallbackFooter: 'Bitte prüfen Sie die Lieferung auf Vollständigkeit.'
  },
  partial_invoice: {
    titleKey: 'partial_invoice_document_title',
    introKey: 'partial_invoice_intro_text',
    footerKey: 'partial_invoice_footer_text',
    paymentTermsKey: 'payment_terms',
    numberLabel: 'Abschlagsnr.',
    fallbackTitle: 'Abschlagsrechnung',
    fallbackIntro: 'Wir stellen Ihnen folgenden Abschlagsbetrag in Rechnung.',
    fallbackFooter: 'Der Restbetrag wird nach Fertigstellung berechnet.',
    fallbackPaymentTerms:
      'Bitte überweisen Sie den Abschlagsbetrag innerhalb der vereinbarten Zahlungsfrist auf das untenstehende Konto.'
  },
  credit_note: {
    titleKey: 'credit_note_document_title',
    introKey: 'credit_note_intro_text',
    footerKey: 'credit_note_footer_text',
    numberLabel: 'Korrekturnr.',
    fallbackTitle: 'Rechnungskorrektur',
    fallbackIntro: 'Wir korrigieren Ihre Rechnung wie folgt.',
    fallbackFooter: 'Bitte entschuldigen Sie die entstandenen Unannehmlichkeiten.'
  },
  recurring_invoice: {
    titleKey: 'recurring_invoice_document_title',
    introKey: 'recurring_invoice_intro_text',
    footerKey: 'recurring_invoice_footer_text',
    paymentTermsKey: 'payment_terms',
    numberLabel: 'Serienrechnungsnr.',
    fallbackTitle: 'Serienrechnung',
    fallbackIntro: 'Wir berechnen Ihnen die wiederkehrenden Leistungen wie folgt.',
    fallbackFooter: 'Die nächste Abrechnung erfolgt automatisch zum vereinbarten Termin.',
    fallbackPaymentTerms:
      'Bitte begleichen Sie den Rechnungsbetrag entsprechend der vereinbarten Zahlungsmodalitäten.'
  }
};

const getSettingText = (settings: any, key?: string) => {
  if (!key) return '';
  const value = settings?.[key];
  return typeof value === 'string' ? value.trim() : '';
};

const getPaymentMethodLabel = (
  methodKey: string | null | undefined,
  settings: any
): string | null => {
  if (!methodKey) return null;

  const configuredMethod = settings?.payment_methods?.[methodKey];
  if (configuredMethod?.description) {
    return configuredMethod.description;
  }

  const defaultLabels: Record<string, string> = {
    cash: 'Barzahlung',
    bank_transfer: 'Überweisung',
    paypal: 'PayPal',
    credit_card: 'Kreditkarte',
    direct_debit: 'Lastschrift',
    debit_card: 'EC-Karte',
    ec_card: 'EC-Karte',
    sepa_transfer: 'SEPA-Überweisung',
  };

  return defaultLabels[methodKey] || methodKey;
};

// Prüft, ob der Rechnungsstatus einer stornierten Rechnung entspricht, damit die PDF einen klaren Storno-Hinweis zeigen kann.
const isCanceledInvoice = (status: string | null | undefined): boolean => {
  const normalizedStatus = (status ?? '').trim().toLowerCase();
  return normalizedStatus === 'cancelled' || normalizedStatus === 'canceled' || normalizedStatus === 'storniert';
};

export const generateInvoicePdf = async ({
  invoice,
  invoiceItems,
  selectedCustomer,
  organization,
  settings,
  shouldDownload = true,
}: {
  invoice: any;
  invoiceItems: any[];
  selectedCustomer: any;
  organization: any;
  settings: any;
  shouldDownload?: boolean;
}): Promise<Blob> => {
  // Fallback sorgt dafür, dass die PDF auch ohne vollständig ladbaren Kunden-Datensatz erstellt werden kann.
  const customer = {
    first_name: selectedCustomer?.first_name ?? '',
    last_name: selectedCustomer?.last_name ?? '',
    street: selectedCustomer?.street ?? '',
    house_number: selectedCustomer?.house_number ?? '',
    postal_code: selectedCustomer?.postal_code ?? '',
    city: selectedCustomer?.city ?? '',
    customer_number: selectedCustomer?.customer_number ?? '',
  };

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const globalPriceDisplayMode: InvoicePriceDisplayMode = settings?.invoice_price_display_mode === 'gross' ? 'gross' : 'net';

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  doc.setFont('helvetica', 'normal');

  const documentType = detectDocumentType(invoice);
  const textConfig = DOCUMENT_TEXT_CONFIG[documentType] ?? DOCUMENT_TEXT_CONFIG.invoice;
  const invoiceTitleOverride = typeof invoice?.document_title === 'string' ? invoice.document_title.trim() : '';
  const invoiceIntroOverride = typeof invoice?.document_intro === 'string' ? invoice.document_intro.trim() : '';
  const invoicePaymentTermsOverride = typeof invoice?.payment_terms === 'string' ? invoice.payment_terms.trim() : '';
  const invoiceFooterOverride = typeof invoice?.document_footer === 'string' ? invoice.document_footer.trim() : '';

  const documentTitle =
    invoiceTitleOverride || getSettingText(settings, textConfig.titleKey) || textConfig.fallbackTitle;
  const documentIntroText =
    invoiceIntroOverride || getSettingText(settings, textConfig.introKey) || textConfig.fallbackIntro;
  const documentPaymentText = textConfig.paymentTermsKey
    ? invoicePaymentTermsOverride ||
      getSettingText(settings, textConfig.paymentTermsKey) ||
      textConfig.fallbackPaymentTerms ||
      ''
    : '';
  const documentFooterText =
    invoiceFooterOverride || getSettingText(settings, textConfig.footerKey) || textConfig.fallbackFooter;
  const documentNumberLabel = textConfig.numberLabel;

  // -------------------------
  // HEADER (schlicht, ohne Farbfläche)
  // -------------------------
  let logoWidth = 0;
  let logoHeight = 0;
  if (organization.logo_url) {
    try {
      const img = new Image();
      img.src = organization.logo_url;
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
      });

      const aspectRatio = img.width / img.height || 1;
      const maxWidth = 72;
      const maxHeight = 30;
      logoWidth = Math.min(maxWidth, maxHeight * aspectRatio);
      logoHeight = logoWidth / aspectRatio;
      if (logoHeight > maxHeight) {
        logoHeight = maxHeight;
        logoWidth = logoHeight * aspectRatio;
      }

      // Verwendet die gespeicherte Organisations-Skalierung für das Rechnungslogo.
      const scaledLogo = scaleLogoDimensions(logoWidth, logoHeight, organization);
      logoWidth = scaledLogo.width;
      logoHeight = scaledLogo.height;
      doc.addImage(img, 'PNG', margin, margin, logoWidth, logoHeight);
    } catch (error) {
      console.error('Fehler beim Laden des Logos', error);
    }
  }

  const senderLines = [
    organization.name,
    joinAddress([organization.street, organization.house_number]),
    joinAddress([organization.postal_code, organization.city]),
  ].filter(Boolean) as string[];
  // Optionales RS-Codewerk-Flag: Absenderadresse als Einzeiler direkt über dem Empfängerblock darstellen.
  const renderSenderAsSingleLine = Boolean(settings?.sender_address_single_line);
  const senderSingleLine = senderLines.join(', ');

  // RECHTS: kompakter Datenblock (statt farbiger Box)  // CHANGED
  const infoX = pageWidth - margin - 78;
  const infoY = margin;
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(documentTitle, infoX + 78, infoY + 2, { align: 'right' });

  const invoiceInfo: { label: string; value: string }[] = [
    { label: documentNumberLabel, value: invoice.invoice_number || '—' },
  ];

  if (invoice.kv_number) {
    invoiceInfo.push({ label: 'KV-Nr.', value: String(invoice.kv_number) });
  }

  if (customer.customer_number) {
    invoiceInfo.push({ label: 'Kundennr.', value: customer.customer_number });
  }

  if (invoice.invoice_date) {
    invoiceInfo.push({ label: 'Rechnungsdatum', value: new Date(invoice.invoice_date).toLocaleDateString('de-DE') });
  }

  if (invoice.due_date) {
    invoiceInfo.push({ label: 'Fällig bis', value: new Date(invoice.due_date).toLocaleDateString('de-DE') });
  }

  const paymentMethodLabel = getPaymentMethodLabel(invoice.payment_method, settings);
  if (paymentMethodLabel) {
    invoiceInfo.push({ label: 'Zahlungsart', value: paymentMethodLabel });
  }

  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  let iy = infoY + 8;
  invoiceInfo.forEach(({ label, value }) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, infoX, iy);
    doc.setFont(undefined, 'normal');
    doc.text(value, infoX + 78, iy, { align: 'right' });
    iy += 5.5;
  });

  // Trennlinie
  const headerBottom = Math.max(margin + logoHeight + 12, iy + 4);
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, headerBottom, pageWidth - margin, headerBottom);

  // -------------------------
  // EMPFÄNGERBLOCK
  // -------------------------
  let y = headerBottom + 10;
  if (senderLines.length) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    if (renderSenderAsSingleLine && senderSingleLine) {
      // Gibt den Absender als kompakten Einzeiler aus, damit er direkt über dem Empfänger sitzt.
      doc.text(senderSingleLine, margin, y);
      y += 5;
    } else {
      senderLines.forEach(line => {
        doc.text(line, margin, y);
        y += 4;
      });
      y += 4;
    }
  }
  doc.setFontSize(11);
  doc.setTextColor(30, 41, 59);
  doc.setFont(undefined, 'bold');
  doc.text(`${customer.first_name} ${customer.last_name}`.trim() || 'Kunde', margin, y);
  y += 6;
  doc.setFont(undefined, 'normal');
  const customerStreet = joinAddress([customer.street, customer.house_number]);
  const customerCity = joinAddress([customer.postal_code, customer.city]);
  if (customerStreet) { doc.text(customerStreet, margin, y); y += 5.5; }
  if (customerCity)   { doc.text(customerCity, margin, y);   y += 5.5; }
  // Einleitung
  y += 3;
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  const introLines = doc.splitTextToSize(documentIntroText, pageWidth - margin * 2);
  doc.text(introLines, margin, y);
  y += introLines.length * 4.5;

  // -------------------------
  // POSITIONSTABELLE (neutral grau)  // CHANGED
  // -------------------------
  const normalizedTableItems = normalizeInvoiceItems(invoiceItems);
  const tableColumns = ['Pos.', 'Bezeichnung', 'Menge', 'Einzel €', 'USt. %', 'Rabatt €', 'Gesamt €'];
  const tableRows = normalizedTableItems.map((item: any, idx: number) => {
    const taxRate = Number(item.tax_rate || 0);
    const vatFactor = 1 + taxRate / 100;
    const rowMode: InvoicePriceDisplayMode =
      item.is_gross === true ? 'gross' : item.is_gross === false ? 'net' : globalPriceDisplayMode;
    const unitPriceNet = Number(item.unit_price || 0);
    const unitPriceDisplay = rowMode === 'gross' ? unitPriceNet * vatFactor : unitPriceNet;
    const lineTotalGross = Number(item.gross_amount || 0);

    return [
      `${idx + 1}`,
      `${item.product_name}${item.description ? `
${item.description}` : ''}`,
      `${item.quantity}`,
      formatCurrency(unitPriceDisplay),
      `${taxRate}%`,
      formatCurrency(Number(item.discount_amount || 0)),
      formatCurrency(lineTotalGross),
    ];
  });

  doc.autoTable({
    head: [tableColumns],
    body: tableRows,
    startY: y + 7,
    styles: { fontSize: 9, cellPadding: { top: 2.2, right: 2, bottom: 2.2, left: 2 }, textColor: [51, 65, 85] },
    headStyles: {
      fillColor: [243, 244, 246], // grau statt blau
      textColor: [17, 24, 39],
      fontStyle: 'bold',
      halign: 'left',
      lineWidth: 0.2,
      lineColor: [209, 213, 219],
    },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center', overflow: 'visible' },
      1: { cellWidth: 62, halign: 'left', overflow: 'linebreak' },
      2: { cellWidth: 16, halign: 'center', overflow: 'visible' },
      3: { cellWidth: 24, halign: 'right', overflow: 'visible' },
      4: { cellWidth: 14, halign: 'right' },
      5: { cellWidth: 20, halign: 'right' },
      6: { cellWidth: 22, halign: 'right' },
    },
    margin: { left: margin, right: margin },
    tableLineColor: [209, 213, 219],
    tableLineWidth: 0.2,
  });

  const tableBottom = (doc as any).lastAutoTable?.finalY || y + 25;

  // -------------------------
  // SUMMENBLOCK rechts als kleine Tabelle  // CHANGED
  // -------------------------
  const summary = calculateInvoiceSummary({
    items: invoiceItems,
    voucherAmount: Number(invoice.voucher_amount) || 0,
    invoiceDiscount: Number(invoice.discount_amount) || 0,
    paymentMethodFee: Number(invoice.payment_fee) || 0,
    paymentMethodFeeTaxRate: 19,
  });

  type Line = { label: string; value: string; bold?: boolean };
  const lines: Line[] = [
    { label: 'Zwischensumme (vor Rabatt)', value: formatCurrency(summary.subtotalBeforeDiscount) }
  ];
  if (summary.itemDiscountTotal) {
    lines.push({ label: 'abzgl. Positionsrabatte', value: `-${formatCurrency(summary.itemDiscountTotal)}` });
  }
  lines.push({ label: 'Zwischensumme netto', value: formatCurrency(summary.subtotal) });
  if (summary.voucherAmount) lines.push({ label: 'abzgl. Gutschein', value: `-${formatCurrency(summary.voucherAmount)}` });
  if (summary.normalizedInvoiceDiscount) {
    lines.push({ label: 'abzgl. Rechnungsrabatt', value: `-${formatCurrency(summary.normalizedInvoiceDiscount)}` });
  }
  if (summary.paymentMethodFee) lines.push({ label: 'zzgl. Zahlungsgebühr', value: formatCurrency(summary.paymentMethodFee) });
  lines.push({ label: 'Netto gesamt', value: formatCurrency(summary.totalNet) });

  Object.entries(summary.taxAmounts)
    .sort(([a], [b]) => Number(a) - Number(b))
    .forEach(([rate, amount]) => {
      lines.push({ label: `zzgl. MwSt. ${rate}%`, value: formatCurrency(amount) });
    });

  lines.push({ label: 'Gesamtbetrag', value: formatCurrency(summary.totalGross), bold: true });

  const sumX = pageWidth - margin - 90;
  const sumY = tableBottom + 8;

  doc.autoTable({
    startY: sumY,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2.2, textColor: [51, 65, 85] },
    body: lines.map(l => [
      { content: l.label, styles: { fontStyle: l.bold ? 'bold' : 'normal' } },
      { content: l.value, styles: { halign: 'right', fontStyle: l.bold ? 'bold' : 'normal' } },
    ]),
    margin: { left: sumX, right: margin },
    didDrawCell: (data) => {
      // Rahmen unten leicht
      const { cursor } = (data as any).table;
      doc.setDrawColor(229, 231, 235);
      if (data.column.index === 1) {
        doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
      }
    },
  });

  // Nutzt die tatsächlich gerenderte Tabellenhöhe, damit unterhalb von „Gesamtbetrag“ stets genug Abstand bleibt.
  const summaryTableBottom = (doc as any).lastAutoTable?.finalY || (sumY + lines.length * 6.2);
  let contentBottom = Math.max(summaryTableBottom, tableBottom);

  // -------------------------
  // EPC-QR links mit Legende
  // -------------------------
  if (organization.iban && summary.totalGross > 0) {
    try {
      const creditorName = organization.account_holder || organization.name || '';
      const remittance = `${documentTitle} ${invoice.invoice_number || 'Entwurf'}`;
      const payload = buildEpcPayload({
        bic: organization.bic,
        creditorName,
        iban: organization.iban,
        amount: Number(summary.totalGross),
        remittance,
      });
      const qrDataUrl = await QRCode.toDataURL(payload, { errorCorrectionLevel: 'M', margin: 1, scale: 4 });
      const qrX = margin;
      const qrY = tableBottom + 8;
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, 40, 40);
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('SEPA-Überweisung per QR-Code', qrX + 20, qrY + 44, { align: 'center' });
      contentBottom = Math.max(contentBottom, qrY + 48);
    } catch (e) {
      console.error('Fehler beim QR-Code', e);
    }
  }

  // -------------------------
  // ZAHLUNGS- UND HINWEISTEXTE
  // -------------------------
  // Zusätzlicher vertikaler Puffer verhindert Überlappung mit dem Hinweistext unter der Summenbox.
  let currentY = contentBottom + 12;
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);

  if (documentPaymentText) {
    const paymentLines = doc.splitTextToSize(documentPaymentText, pageWidth - margin * 2);
    doc.text(paymentLines, margin, currentY);
    currentY += paymentLines.length * 4.5 + 6;
  }

  if (documentFooterText) {
    const footerLines = doc.splitTextToSize(documentFooterText, pageWidth - margin * 2);
    doc.text(footerLines, margin, currentY);
    currentY += footerLines.length * 4.5 + 6;
  }

  if (invoice.notes) {
    doc.setFont(undefined, 'bold'); doc.setTextColor(51, 65, 85);
    doc.text('Hinweise', margin, currentY); currentY += 5;
    doc.setFont(undefined, 'normal'); doc.setTextColor(71, 85, 105);
    const noteLines = doc.splitTextToSize(String(invoice.notes), pageWidth - margin * 2);
    doc.text(noteLines, margin, currentY);
    currentY += noteLines.length * 4.5 + 6;
  }

  // -------------------------
  // DREISPALTIGER FOOTER  // CHANGED
  // -------------------------
  const footerTop = pageHeight - 36;
  doc.setDrawColor(226, 232, 240);
  doc.line(0, footerTop, pageWidth, footerTop);

  const colW = (pageWidth - margin * 2) / 3;
  const col1X = margin;
  const col2X = margin + colW;
  const col3X = margin + 2 * colW;
  const fY = footerTop + 7;

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);

  // 1) Unternehmen & Adresse
  const companyLines: string[] = [];
  if (organization.name) companyLines.push(organization.name);
  const companyAddressStreet = joinAddress([organization.street, organization.house_number]);
  const companyAddressCity = joinAddress([organization.postal_code, organization.city]);
  if (companyAddressStreet) companyLines.push(companyAddressStreet);
  if (companyAddressCity) companyLines.push(companyAddressCity);

  const drawColumn = (lines: string[], x: number) => {
    let yPos = fY;
    lines.filter(Boolean).forEach(line => {
      const wrapped = doc.splitTextToSize(line, colW - 6);
      wrapped.forEach((textLine: string) => {
        doc.text(textLine, x, yPos);
        yPos += 4;
      });
    });
  };

  drawColumn(companyLines, col1X);

  // 2) Kontakt
  const contactLines: string[] = [];
  if (organization.phone) contactLines.push(`Tel.: ${organization.phone}`);
  if (organization.email) contactLines.push(`E-Mail: ${organization.email}`);
  if (organization.website) contactLines.push(`Web: ${organization.website}`);
  if (settings?.tax_id) contactLines.push(`Steuernummer: ${settings.tax_id}`);
  if (settings?.register) contactLines.push(`Handelsregister: ${settings.register}`);
  drawColumn(contactLines, col2X);

  // 3) Bank
  const bankLines: string[] = [];
  if (organization.name && !organization.account_holder) bankLines.push(organization.name);
  if (organization.account_holder) bankLines.push(`Kontoinhaber: ${organization.account_holder}`);
  if (organization.bank_name) bankLines.push(organization.bank_name);
  if (organization.iban) bankLines.push(`IBAN: ${organization.iban}`);
  if (organization.bic) bankLines.push(`BIC: ${organization.bic}`);
  drawColumn(bankLines, col3X);

  // Seitenzahl (dezent)
  doc.setTextColor(148, 163, 184);
  doc.text(`Seite 1/1`, pageWidth - margin, pageHeight - 6, { align: 'right' });

  // Hebt stornierte Rechnungen im gesamten Export mit einem gut sichtbaren diagonalen Storno-Stempel hervor.
  if (isCanceledInvoice(invoice?.status)) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(58);
    doc.setTextColor(220, 38, 38);
    doc.text('STORNIERT', pageWidth / 2, pageHeight / 2-30, {
      align: 'center',
      angle: 325,
    });
  }

  // -------------------------
  // DOWNLOAD
  // -------------------------
  const pdfOutput = doc.output('blob');
  if (shouldDownload) {
    const url = URL.createObjectURL(pdfOutput);
    const link = document.createElement('a');
    const sanitizedTitle = removeDiacritics(documentTitle).replace(/[^a-zA-Z0-9_-]+/g, '_');
    const fileTitle = sanitizedTitle.length > 0 ? sanitizedTitle : 'Dokument';
    link.href = url;
    link.download = `${fileTitle}_${invoice.invoice_number || 'Entwurf'}.pdf`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);

    // Startet primär den Download und nutzt auf Touch-Geräten zusätzlich ein Öffnen im Browser-Tab,
    // weil iOS/Android den `download`-Hinweis je nach Browser teilweise ignorieren.
    link.click();
    if (isTouchDevice()) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 1000);
  }

  return pdfOutput;
};
